import React, { Component } from 'react'
import { Alert, Button, Card, Checkbox, Empty, Modal, Radio, Select, Space, Spin, Steps, Table, Tag, Typography, message } from 'antd'
import { CopyOutlined, PlusOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import {
    getUserDeparts,
    getUserListForDropbox,
    getVibeOwnerAccessContext,
    getVibeOwnerAccessProjects,
    getVibeOwnerAccessStatus,
    saveVibeOwnerPagePermission,
} from '../../request'
import './index.less'

const { Title, Text, Paragraph } = Typography

const formatTime = value => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    const pad = number => String(number).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default class VibeOwnerAccess extends Component {
    state = {
        loading: true, error: '', projects: [], projectId: undefined, context: null, statusContext: null,
        selectedRouteIds: [], accessMode: undefined, subjectId: undefined,
        includeChildren: true, users: [], departments: [], saving: false, grantEditing: false,
    }

    componentDidMount() {
        const params = new URLSearchParams(this.props.location?.search || '')
        this.requestedProjectId = params.get('projectId')
        this.statusView = params.get('view') === 'status'
        if (this.statusView) {
            this.loadStatusContext()
            return
        }
        this.loadProjects()
    }

    loadStatusContext = async () => {
        if (!/^\d+$/.test(this.requestedProjectId || '')) {
            this.setState({ loading: false, error: '应用信息不完整，请返回工作台后重试' })
            return
        }
        this.setState({ loading: true, error: '' })
        try {
            const statusContext = await getVibeOwnerAccessStatus({ projectId: this.requestedProjectId })
            this.setState({ loading: false, statusContext })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用状态加载失败，请稍后重试' })
        }
    }

    loadProjects = async () => {
        this.setState({ loading: true, error: '' })
        try {
            const projects = await getVibeOwnerAccessProjects() || []
            const requested = projects.find(item => String(item.id) === String(this.requestedProjectId))
            const project = requested || projects[0]
            this.setState({ projects, projectId: project?.id, loading: false }, () => {
                if (project) this.loadContext(project.id)
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用列表加载失败，请稍后重试' })
        }
    }

    loadContext = async (projectId = this.state.projectId) => {
        if (!projectId) return
        this.setState({ loading: true, error: '', projectId })
        try {
            const context = await getVibeOwnerAccessContext({ projectId })
            const pageRoutes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
            this.setState({
                context,
                selectedRouteIds: pageRoutes.map(route => route.id),
                accessMode: undefined,
                subjectId: undefined,
                grantEditing: !context?.accessConfigured,
                loading: false,
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '权限设置加载失败，请稍后重试' })
        }
    }

    loadOptions = async mode => {
        this.setState({ accessMode: mode, subjectId: undefined })
        if (mode === 'ALL' || (this.state.users.length && this.state.departments.length)) return
        try {
            const [users, departments] = await Promise.all([
                getUserListForDropbox({ type: 0 }), getUserDeparts({}),
            ])
            this.setState({ users: users || [], departments: departments || [] })
        } catch (error) {
            message.error(error?.message || '授权对象加载失败')
        }
    }

    copyPrompt = async () => {
        try {
            await navigator.clipboard.writeText(this.state.context?.routeSyncPrompt || '')
            message.success('页面同步提示词已复制，请在项目 Codex 中执行')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    saveGrant = async () => {
        const { projectId, selectedRouteIds, accessMode, subjectId, includeChildren } = this.state
        if (!accessMode) {
            message.warning('请选择访问范围')
            return
        }
        if (!subjectId && accessMode !== 'ALL') {
            message.warning('请选择要授权的员工或部门')
            return
        }
        this.setState({ saving: true })
        try {
            await saveVibeOwnerPagePermission({
                projectId, routeIds: selectedRouteIds, subjectType: accessMode,
                subjectId: accessMode === 'ALL' ? '' : String(subjectId), includeChildren, granted: true,
            })
            message.success('权限设置已保存')
            this.setState({ saving: false, grantEditing: false }, () => this.loadContext())
        } catch (error) {
            this.setState({ saving: false })
            message.error(error?.message || '保存失败，请稍后重试')
        }
    }

    allowAllEmployees = () => {
        const routes = (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE')
        Modal.confirm({
            title: '允许全部员工访问所有页面？',
            content: `确认后，全体员工将可以访问当前已登记的 ${routes.length} 个页面。`,
            okText: '确认开放', cancelText: '取消',
            onOk: async () => {
                this.setState({ saving: true })
                try {
                    await saveVibeOwnerPagePermission({
                        projectId: this.state.projectId,
                        routeIds: routes.map(route => route.id),
                        subjectType: 'ALL', subjectId: '', includeChildren: false, granted: true,
                    })
                    message.success('已允许全部员工访问所有页面')
                    this.setState({ saving: false, grantEditing: false }, () => this.loadContext())
                } catch (error) {
                    this.setState({ saving: false })
                    message.error(error?.message || '保存失败，请稍后重试')
                    throw error
                }
            },
        })
    }

    getPermissionRows = (context, routes) => {
        if (!context) return []
        const grouped = new Map()
        routes.forEach(route => {
            ;(route.permissions || []).forEach(permission => {
                const key = `${permission.subjectType}:${permission.subjectId || ''}`
                const existing = grouped.get(key) || {
                    key,
                    subjectType: permission.subjectType,
                    subjectName: permission.subjectName || permission.subjectId,
                    includeChildren: permission.includeChildren,
                    routes: [],
                    createdAt: permission.createdAt,
                }
                existing.routes.push(route)
                if (permission.createdAt && (!existing.createdAt || String(permission.createdAt) < String(existing.createdAt))) {
                    existing.createdAt = permission.createdAt
                }
                grouped.set(key, existing)
            })
        })
        return [{
            key: 'OWNER', subjectType: 'OWNER',
            subjectName: context.ownerUserNick || '项目创建人',
            routes, createdAt: context.createTime,
        }, ...Array.from(grouped.values())]
    }

    renderGrantForm = routes => <div className="vibe-owner-access-editor">
        <div className="vibe-owner-access-editor-heading">
            <div><Title level={5}>新增页面访问权限</Title><Text type="secondary">选择页面和访问对象，保存后立即生效。</Text></div>
            {this.state.context?.accessConfigured && <Button onClick={() => this.setState({ grantEditing: false })}>取消</Button>}
        </div>
        <div className="vibe-owner-access-routes"><Title level={5}>选择页面</Title>
            <Checkbox.Group value={this.state.selectedRouteIds} onChange={value => this.setState({ selectedRouteIds: value })}>
                <Space direction="vertical">{routes.map(route => <Checkbox key={route.id} value={route.id}>{route.routeName} <Text type="secondary">{route.pathPattern}</Text></Checkbox>)}</Space>
            </Checkbox.Group></div>
        <div className="vibe-owner-access-grant"><Title level={5}>访问范围</Title>
            <Radio.Group value={this.state.accessMode} onChange={event => this.loadOptions(event.target.value)}>
                <Radio value="ALL">全部员工</Radio>
                <Radio value="USER">指定员工</Radio><Radio value="DEPARTMENT">指定部门</Radio>
            </Radio.Group>
            {this.state.accessMode === 'USER' && <Select showSearch placeholder="选择员工" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                options={this.state.users.map(user => ({ value: user.USER_ID || user.userId, label: user.USER_NICK || user.userNick || user.USER_NAME }))} />}
            {this.state.accessMode === 'DEPARTMENT' && <Space><Select showSearch placeholder="选择部门" value={this.state.subjectId} onChange={subjectId => this.setState({ subjectId })}
                options={this.state.departments.map(dept => ({ value: dept.DING_DEPT_ID || dept.dingDeptId, label: dept.DEPT_NAME || dept.deptName }))} />
                <Checkbox checked={this.state.includeChildren} onChange={event => this.setState({ includeChildren: event.target.checked })}>包含下级部门</Checkbox></Space>}
            <Paragraph type="secondary">项目创建人始终可以访问，无需单独授权。</Paragraph>
        </div>
        <Space>
            <Button type="primary" loading={this.state.saving} disabled={!this.state.selectedRouteIds.length || !this.state.accessMode} onClick={this.saveGrant}>保存权限</Button>
            <Button loading={this.state.saving} onClick={this.allowAllEmployees}>允许全部员工访问所有页面</Button>
        </Space>
    </div>

    renderPermissionList = (context, routes) => {
        const rows = this.getPermissionRows(context, routes)
        const typeMeta = {
            OWNER: { color: 'gold', text: '项目创建人' },
            USER: { color: 'blue', text: '员工' },
            DEPARTMENT: { color: 'purple', text: '部门' },
            ALL: { color: 'green', text: '全部员工' },
        }
        const columns = [{
            title: '访问对象', dataIndex: 'subjectName', width: 220,
            render: (value, row) => <div className="vibe-owner-access-subject"><Text strong>{value}</Text>
                <Tag color={typeMeta[row.subjectType]?.color}>{typeMeta[row.subjectType]?.text || row.subjectType}</Tag>
                {row.subjectType === 'DEPARTMENT' && row.includeChildren ? <Text type="secondary">包含下级部门</Text> : null}</div>,
        }, {
            title: '可访问页面', dataIndex: 'routes',
            render: routeList => <div className="vibe-owner-access-page-tags">{routeList.map(route =>
                <Tag key={route.id}>{route.routeName}<span>{route.pathPattern}</span></Tag>)}</div>,
        }, {
            title: '添加时间', dataIndex: 'createdAt', width: 170,
            render: value => <Text type="secondary">{formatTime(value)}</Text>,
        }]
        return <div className="vibe-owner-access-list">
            <div className="vibe-owner-access-list-heading">
                <div><Title level={5}>当前访问权限</Title><Text type="secondary">共 {rows.length} 个访问对象，覆盖 {routes.length} 个页面</Text></div>
                <Space><Button disabled={!context?.entryUrl} onClick={() => window.location.assign(context.entryUrl)}>打开应用</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => this.setState({ grantEditing: true })}>新增授权</Button></Space>
            </div>
            <Table rowKey="key" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 760 }} />
        </div>
    }

    renderStatus = () => {
        const { loading, error, statusContext } = this.state
        return (
        <div className="vibe-owner-access-page"><Card className="vibe-owner-access-status">
            <Spin spinning={loading}>
                <SafetyCertificateOutlined className="vibe-owner-access-status-icon" />
                <Title level={3}>你还不能访问这个页面</Title>
                {error ? <Alert type="error" showIcon message={error} /> : <>
                    <Paragraph type="secondary">{statusContext?.projectName || '该应用'}正在准备访问设置，或你尚未获得页面权限。</Paragraph>
                    {statusContext?.ownerUserNick && <Paragraph>应用负责人：{statusContext.ownerUserNick}</Paragraph>}
                </>}
                <Space className="vibe-owner-access-status-actions">
                    <Button type="primary" href="/portal/dashboard">返回工作台</Button>
                    <Button disabled={!statusContext?.entryUrl} onClick={() => window.location.assign(statusContext.entryUrl)}>重新检查访问权限</Button>
                    {error && <Button onClick={this.loadStatusContext}>重试加载</Button>}
                </Space>
            </Spin>
        </Card></div>
        )
    }

    render() {
        if (this.statusView) return this.renderStatus()
        const { loading, error, projects, projectId, context, grantEditing } = this.state
        const routes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
        return <div className="vibe-owner-access-page">
            <div className="vibe-owner-access-heading"><Title level={3}>应用权限设置</Title>
                <Text type="secondary">查看并维护员工可访问的应用页面。</Text></div>
            <Card bordered={false}>
                <Spin spinning={loading}>
                    {error && <Alert type="error" showIcon message={error} action={<Button onClick={this.loadProjects}>重试</Button>} />}
                    {!error && !loading && !projects.length && <Empty description="你还没有可配置的内部应用" />}
                    {!!projects.length && <>
                        <div className="vibe-owner-access-project"><Text>我创建的应用</Text>
                            <Select value={projectId} onChange={this.loadContext} style={{ minWidth: 260 }}
                                options={projects.map(item => ({ value: item.id, label: item.projectName }))} /></div>
                        {!context?.accessConfigured && <Steps current={routes.length ? 1 : 0} items={[{ title: '同步应用页面' }, { title: '设置访问范围' }, { title: '完成配置' }]} />}
                        {context && !routes.length && <Alert className="vibe-owner-access-alert" type="warning" showIcon
                            message="还差一步即可开放应用"
                            description="尚未检测到应用页面。复制提示词到项目 Codex 执行，完成后重新检测。"
                            action={<Space direction="vertical"><Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制页面同步提示词</Button>
                                <Button icon={<ReloadOutlined />} onClick={() => this.loadContext()}>我已同步，重新检测</Button></Space>} />}
                        {!!routes.length && context?.accessConfigured && this.renderPermissionList(context, routes)}
                        {!!routes.length && grantEditing && this.renderGrantForm(routes)}
                    </>}
                </Spin>
            </Card>
        </div>
    }
}
